// src/Dashboard.js
import React, { useState, useEffect } from "react";
import { apiFetch } from "./utils/apiUtils";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Menu,
  MenuItem,
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  CircularProgress,
  IconButton,
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import GroomerPerformance from "./GroomerPerformance";
import SchedulerApp from "./SchedulerApp";
import Documents from "./Documents";
import Maintenance from "./Maintenance";
import CustomReport from "./CustomReport";
import MoegoScheduler from "./MoegoScheduler";
import TodaysTotals from "./TodaysTotals";
import VaccinationReport from "./VaccinationReport";
import FacebookDownloader from "./FacebookDownloader";
import FacebookAITesting from "./FacebookAITesting";
import FacebookMessageManager from "./FacebookMessageManager";
import TaskTracker from "./TaskTracker";
import ChangePassword from "./ChangePassword";
import MarketingStats from "./MarketingStats";
import GroomerMatrix from "./GroomerMatrix";
import ServiceAreaManagement from "./ServiceAreaManagement";
import LoadServiceAreaData from "./LoadServiceAreaData";
import DevNotificationTester from "./components/DevNotificationTester";
import Reports from "./Reports";
import GroomerRebooks from "./GroomerRebooks";
import MarketingLeadsVsBookings from "./MarketingLeadsVsBookings";
import GroomerProductionReport from "./GroomerProductionReport";
import AppointmentsByReferralSource from "./AppointmentsByReferralSource";
import AppointmentsByPetSize from "./AppointmentsByPetSize";
import AppointmentsWithGroomerChange from "./AppointmentsWithGroomerChange";
import AppointmentsByCreationUser from "./AppointmentsByCreationUser";
import BookingCancellations from "./BookingCancellations";

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [applicationsAnchorEl, setApplicationsAnchorEl] = useState(null);
  const [reportsAnchorEl, setReportsAnchorEl] = useState(null);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const [selectedContent, setSelectedContent] = useState("");
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [refreshKey, setRefreshKey] = useState(0); // Add a key to force refresh of dashboard content
  
  // Detect iOS device
  const isIOS = () => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent);
  };
  
  // Check for notification click data stored by the service worker
  const checkForStoredNotificationClicks = () => {
    console.log('[Dashboard] 🔍 Checking for stored notification clicks...');
    try {
      // Track if we've found and processed notification data to avoid duplicates
      let notificationProcessed = false;
      
      // Check IndexedDB for click data with improved error handling
      const checkIndexedDB = async () => {
        if ('indexedDB' in window) {
          try {
            // Create a promise-wrapped version of IndexedDB access for better control
            const getIndexedDBData = () => {
              return new Promise((resolve, reject) => {
                try {
                  const dbOpenRequest = indexedDB.open('zg-notifications', 1);
                  
                  dbOpenRequest.onupgradeneeded = (event) => {
                    const db = event.target.result;
                    if (!db.objectStoreNames.contains('clicks')) {
                      db.createObjectStore('clicks', { keyPath: 'timestamp' });
                    }
                  };
                  
                  dbOpenRequest.onsuccess = (event) => {
                    try {
                      const db = event.target.result;
                      
                      // Handle case where the object store doesn't exist yet
                      if (!db.objectStoreNames.contains('clicks')) {
                        console.log('[Dashboard] Clicks store not found in IndexedDB');
                        resolve(null);
                        return;
                      }
                      
                      const transaction = db.transaction(['clicks'], 'readwrite');
                      const store = transaction.objectStore('clicks');
                      const request = store.getAll();
                      
                      request.onsuccess = () => {
                        const clicksData = request.result;
                        // Clear the click data immediately to prevent reopening
                        store.clear().onsuccess = () => {
                          console.log('[Dashboard] IndexedDB click store cleared');
                        };
                        
                        resolve(clicksData);
                      };
                      
                      request.onerror = (error) => {
                        console.error('[Dashboard] Error getting IndexedDB clicks:', error);
                        reject(error);
                      };
                    } catch (innerError) {
                      console.error('[Dashboard] Error in IndexedDB onsuccess handler:', innerError);
                      reject(innerError);
                    }
                  };
                  
                  dbOpenRequest.onerror = (error) => {
                    console.error('[Dashboard] Error opening IndexedDB:', error);
                    reject(error);
                  };
                } catch (outerError) {
                  console.error('[Dashboard] Error in IndexedDB setup:', outerError);
                  reject(outerError);
                }
              });
            };
            
            // Execute the promise-wrapped function
            const clicksData = await getIndexedDBData();
            
            if (clicksData && clicksData.length > 0 && !notificationProcessed) {
              console.log('[Dashboard] 📦 Found notification click data in IndexedDB:', clicksData);
              // Sort by timestamp to get the most recent click
              clicksData.sort((a, b) => b.timestamp - a.timestamp);
              const mostRecentClick = clicksData[0];
              
              // Process the click data (open task, etc.)
              if (mostRecentClick.taskId) {
                console.log('[Dashboard] 🚀 Processing task ID from IndexedDB:', mostRecentClick.taskId);
                notificationProcessed = true;
                
                // Set TaskTracker as active content with this taskId
                setSelectedContent("taskTracker");
                return true;
              }
            }
          } catch (error) {
            console.error('[Dashboard] Error in IndexedDB processing:', error);
          }
        }
        return false;
      };
      
      // Check localStorage for click data
      const checkLocalStorage = () => {
        try {
          // Check for standard notification click data
          const clickDataString = localStorage.getItem('zg-notification-click');
          if (clickDataString && !notificationProcessed) {
            console.log('[Dashboard] 📦 Found notification click data in localStorage:', clickDataString);
            try {
              const clickData = JSON.parse(clickDataString);
              
              // Clean up the storage to prevent reopening on refresh
              localStorage.removeItem('zg-notification-click');
              
              // Process the click data
              if (clickData && clickData.taskId) {
                console.log('[Dashboard] 🚀 Processing task ID from localStorage:', clickData.taskId);
                notificationProcessed = true;
                setSelectedContent("taskTracker");
                return true;
              }
            } catch (error) {
              console.error('[Dashboard] Error parsing notification click data:', error);
            }
          }
          
          // iOS-specific: check for alternative storage keys
          const keys = Object.keys(localStorage);
          const notificationKeys = keys.filter(key => 
            key.startsWith('zg-notification') || 
            key.includes('notification') || 
            key.includes('taskId')
          );
          
          for (const key of notificationKeys) {
            if (key === 'zg-notification-click') continue; // Already checked above
            
            try {
              const value = localStorage.getItem(key);
              console.log(`[Dashboard] 🔑 Checking alternative localStorage key: ${key} = ${value}`);
              
              if (value && !notificationProcessed) {
                let taskId = null;
                
                // Try to parse as JSON
                try {
                  const parsedData = JSON.parse(value);
                  taskId = parsedData.taskId || parsedData.id;
                } catch {
                  // If not JSON, check if it's a direct taskId
                  if (/^\d+$/.test(value)) {
                    taskId = value;
                  }
                }
                
                if (taskId) {
                  console.log(`[Dashboard] 🚀 Found taskId ${taskId} in localStorage key ${key}`);
                  localStorage.removeItem(key); // Clean up
                  notificationProcessed = true;
                  setSelectedContent("taskTracker");
                  return true;
                }
              }
            } catch (error) {
              console.error(`[Dashboard] Error processing localStorage key ${key}:`, error);
            }
          }
        } catch (error) {
          console.error('[Dashboard] Error checking localStorage:', error);
        }
        return false;
      };
            
      // Execute all checks
      const runAllChecks = async () => {
        // First: Check IndexedDB
        if (await checkIndexedDB()) return;
        
        // Second: Check localStorage
        if (checkLocalStorage()) return;
        
        console.log('[Dashboard] No notification click data found');
      };
      
      // Start the checks
      runAllChecks();
      
    } catch (error) {
      console.error('[Dashboard] Error checking for stored notification clicks:', error);
    }
  };
  
  // Listen for notification click messages from service worker
  const listenForNotificationClicks = () => {
    console.log('[Dashboard] Setting up notification click listener');
    
    if ('serviceWorker' in navigator) {
      // Add event listener for "message" events from the service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
          console.log('[Dashboard] 📦 Received notification click data:', event.data);
          const clickData = event.data.payload;
          
          // If the click data includes a taskId, open that task
          if (clickData && clickData.taskId) {
            console.log('[Dashboard] 🚀 Opening task from notification click:', clickData.taskId);
            
            // Set TaskTracker as active content
            setSelectedContent("taskTracker");
          }
        }
      });
    }
  };
  
  // Load username from localStorage when component mounts and check for taskId parameter
  useEffect(() => {
    // Load username
    const storedUsername = localStorage.getItem("username");
    if (storedUsername) {
      setUsername(storedUsername);
    }
    
    // Check URL for taskId in two possible formats:
    // 1. Query parameter format: /?taskId=123
    const urlParams = new URLSearchParams(window.location.search);
    const taskIdFromQuery = urlParams.get('taskId');
    
    // 2. Path-based format: /task/123 (for iOS notifications)
    const pathSegments = window.location.pathname.split('/');
    let taskIdFromPath = null;
    
    // Check if path follows the /task/:id pattern
    const taskPathIndex = pathSegments.findIndex(segment => segment === 'task');
    if (taskPathIndex !== -1 && taskPathIndex < pathSegments.length - 1 && pathSegments[taskPathIndex + 1]) {
      taskIdFromPath = pathSegments[taskPathIndex + 1];
      console.log(`[Dashboard] 🔍 Found taskId in path: ${taskIdFromPath}`);
    }
    
    // Enhanced notification check for iOS - look for notification=true parameter
    const isNotification = urlParams.get('notification') === 'true';
    if (isNotification) {
      console.log('[Dashboard] 🔔 Detected notification=true parameter, likely opened from a notification');
    }
    
    // Use taskId from either source (query parameter takes precedence if both exist)
    const taskId = taskIdFromQuery || taskIdFromPath;
    
    // Set TaskTracker as active content if a taskId was found
    if (taskId) {
      setSelectedContent("taskTracker");
      console.log(`[Dashboard] 🚀 Setting TaskTracker as active content with taskId: ${taskId}`);
    }
    
    // Set up notification click listener regardless of taskId
    listenForNotificationClicks();
    
    // Immediately check for stored notification clicks
    checkForStoredNotificationClicks();
    
    // Set up recurring checks for iOS devices
    const isIOSDevice = isIOS();
    console.log(`[Dashboard] 📱 iOS device detected: ${isIOSDevice}`);
    
    if (isIOSDevice) {
      // Enhanced visibility change detection for iOS
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          console.log('[Dashboard] 👁️ Document became visible, checking for notification clicks');
          checkForStoredNotificationClicks();
        }
      };
      
      // iOS-specific: also check on focus events which can be more reliable on iOS
      const handleWindowFocus = () => {
        console.log('[Dashboard] 🔍 Window focused, checking for notification clicks');
        setTimeout(checkForStoredNotificationClicks, 300); // Short delay for reliability
      };
      
      // iOS Safari sometimes needs a pageshow event
      const handlePageShow = (event) => {
        // The persisted property is true if the page is loaded from the back/forward cache
        console.log('[Dashboard] 📄 Page show event, checking for notification clicks');
        setTimeout(checkForStoredNotificationClicks, 500);
      };
      
      // Set up polling for notification data
      const pollInterval = setInterval(() => {
        console.log('[Dashboard] ⏱️ Polling for notification clicks');
        checkForStoredNotificationClicks();
      }, 3000); // Check every 3 seconds
      
      // Add event listeners for iOS-specific events
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleWindowFocus);
      window.addEventListener('pageshow', handlePageShow);
      
      // Clean up event listeners and interval on unmount
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleWindowFocus);
        window.addEventListener('pageshow', handlePageShow);
        clearInterval(pollInterval);
      };
    }
  }, []);
  
  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleApplicationsMenuOpen = (event) => {
    setApplicationsAnchorEl(event.currentTarget);
  };
  const handleApplicationsMenuClose = () => {
    setApplicationsAnchorEl(null);
  };
  const handleApplicationsSelect = (option) => {
    setSelectedContent(option);
    setApplicationsAnchorEl(null);
  };

  const handleReportsMenuOpen = (event) => {
    setReportsAnchorEl(event.currentTarget);
  };
  const handleReportsMenuClose = () => {
    setReportsAnchorEl(null);
  };
  const handleReportsSelect = (option) => {
    setSelectedContent(option);
    setReportsAnchorEl(null);
  };

  const handleDocumentsSelect = () => {
    setSelectedContent("documents");
  };

  const [maintenanceAnchorEl, setMaintenanceAnchorEl] = useState(null);

  const handleMaintenanceMenuOpen = (event) => {
    setMaintenanceAnchorEl(event.currentTarget);
  };

  const handleMaintenanceMenuClose = () => {
    setMaintenanceAnchorEl(null);
  };

  const handleMaintenanceSelect = (option) => {
    setSelectedContent(option);
    setMaintenanceAnchorEl(null);
  };

  const handleDashboardSelect = () => {
    // If already on dashboard, increment the refresh key to force a reload of dashboard data
    if (selectedContent === "") {
      setRefreshKey(prevKey => prevKey + 1);
      console.log("[Dashboard] 🔄 Refreshing dashboard data...");
    }
    setSelectedContent("");
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleChangePasswordClick = () => {
    setUserMenuAnchorEl(null);
    setChangePasswordOpen(true);
  };

  const handleChangePasswordClose = () => {
    setChangePasswordOpen(false);
  };

  const handleLogout = () => {
    // Clear user session
    localStorage.removeItem("token");
    window.location.reload();
  };

  // ✅ FIXED: Properly handling "customReport" and avoiding duplicate cases
  const renderContent = () => {
    switch (selectedContent) {
      case "scheduler":
        return <SchedulerApp />;
      case "taskTracker":
        // Check both URL formats for taskId
        // 1. Query parameter format: /?taskId=123
        const urlParams = new URLSearchParams(window.location.search);
        const taskIdFromQuery = urlParams.get('taskId');
        
        // 2. Path-based format: /task/123 (for iOS notifications)
        const pathSegments = window.location.pathname.split('/');
        let taskIdFromPath = null;
        
        // Check if path follows the /task/:id pattern
        const taskPathIndex = pathSegments.findIndex(segment => segment === 'task');
        if (taskPathIndex !== -1 && taskPathIndex < pathSegments.length - 1 && pathSegments[taskPathIndex + 1]) {
          taskIdFromPath = pathSegments[taskPathIndex + 1];
        }
        
        // Use taskId from either source (query parameter takes precedence if both exist)
        const taskId = taskIdFromQuery || taskIdFromPath;
        
        return <TaskTracker initialTaskId={taskId} />;
      case "marketingStats":
        return <MarketingStats />;
      case "facebookMessageManager":
        return <FacebookMessageManager />;
      case "otherApp":
        return <Typography variant="h6">Other Application content goes here.</Typography>;
      case "reports":
        return <CustomReport />;
      case "customReport": // ✅ Correctly added customReport
        return <CustomReport />;
      case "vaccinationReport":
        return <VaccinationReport />;
      case "groomerMatrix":
        return <GroomerMatrix />;
      case "groomerRebooks":
        return <GroomerRebooks />;
      case "marketingLeadsVsBookings":
        return <MarketingLeadsVsBookings />;
      case "groomerProduction":
        return <GroomerProductionReport />;
      case "appointmentsByReferral":
        return <AppointmentsByReferralSource />;
      case "appointmentsByPetSize":
        return <AppointmentsByPetSize />;
      case "appointmentsWithGroomerChange":
        return <AppointmentsWithGroomerChange />;
      case "appointmentsByCreationUser":
        return <AppointmentsByCreationUser />;
      case "bookingCancellations":
        return <BookingCancellations />;
      case "documents":
        return <Documents />;
      case "maintenance":
        return <Maintenance type="excel" />;
      case "sqlQueryTool":
        return <Maintenance type="sql" />;
      case "moegoScheduler":
        return <MoegoScheduler />;
      case "facebookDownloader":
        return <FacebookDownloader />;
      /* Facebook AI Response Testing removed - functionality now in Facebook Message Manager */
      case "userManagement":
        return <Maintenance type="users" />;
      case "notificationReset":
        return <Maintenance type="notifications" />;
      case "serviceAreaManagement":
        return <ServiceAreaManagement />;
      case "loadServiceAreaData":
        return <LoadServiceAreaData />;
      case "testNotifications":
        return <DevNotificationTester />;
      default:
        return <DashboardKPI key={refreshKey} />;
    }
  };

  return (
    <Box>
      <AppBar 
        position="static" 
        elevation={0}
        sx={{ 
          minHeight: 48, 
          bgcolor: '#e0e0e0',
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <Toolbar sx={{ minHeight: 48 }}>
          {isMobile ? (
            <>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleMobileMenuToggle}
                sx={{ color: '#666', mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
              <Box sx={{ flexGrow: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ mr: 1, color: '#666' }}>
                  {username}
                </Typography>
                <IconButton
                  onClick={handleUserMenuOpen}
                  sx={{ color: '#666' }}
                >
                  <AccountCircleIcon />
                </IconButton>
              </Box>
            </>
          ) : (
            <>
              <Button
                variant={selectedContent === "" ? "contained" : "text"}
                onClick={handleDashboardSelect}
                sx={{ 
                  mr: 1,
                  bgcolor: selectedContent === "" ? '#1976d2' : 'transparent',
                  color: selectedContent === "" ? 'white' : '#666',
                  '&:hover': {
                    bgcolor: selectedContent === "" ? '#1565c0' : '#f5f5f5'
                  }
                }}
              >
                Dashboard
              </Button>
              <Button
                variant={(selectedContent === "scheduler" || selectedContent === "taskTracker" || selectedContent === "marketingStats" || selectedContent === "facebookMessageManager" || selectedContent === "otherApp") ? "contained" : "text"}
                onClick={handleApplicationsMenuOpen}
                sx={{ 
                  mr: 1,
                  bgcolor: (selectedContent === "scheduler" || selectedContent === "taskTracker" || selectedContent === "marketingStats" || selectedContent === "facebookMessageManager" || selectedContent === "otherApp") ? '#1976d2' : 'transparent',
                  color: (selectedContent === "scheduler" || selectedContent === "taskTracker" || selectedContent === "marketingStats" || selectedContent === "facebookMessageManager" || selectedContent === "otherApp") ? 'white' : '#666',
                  '&:hover': {
                    bgcolor: (selectedContent === "scheduler" || selectedContent === "taskTracker" || selectedContent === "marketingStats" || selectedContent === "facebookMessageManager" || selectedContent === "otherApp") ? '#1565c0' : '#f5f5f5'
                  }
                }}
              >
                Applications
              </Button>
              <Menu
                anchorEl={applicationsAnchorEl}
                open={Boolean(applicationsAnchorEl)}
                onClose={handleApplicationsMenuClose}
              >
                <MenuItem onClick={() => handleApplicationsSelect("scheduler")}>
                  Groomer Scheduler
                </MenuItem>
                <MenuItem onClick={() => handleApplicationsSelect("taskTracker")}>
                  Task Tracker
                </MenuItem>
                <MenuItem onClick={() => handleApplicationsSelect("marketingStats")}>
                  Daily Marketing Statistic
                </MenuItem>
                <MenuItem onClick={() => handleApplicationsSelect("facebookMessageManager")}>
                  Facebook Message Manager
                </MenuItem>
              </Menu>
              <Button
                variant={selectedContent === "reports" || selectedContent === "customReport" || selectedContent === "vaccinationReport" || selectedContent === "groomerMatrix" || selectedContent === "groomerRebooks" || selectedContent === "marketingLeadsVsBookings" || selectedContent === "groomerProduction" || selectedContent === "appointmentsByReferral" || selectedContent === "appointmentsByPetSize" || selectedContent === "appointmentsWithGroomerChange" || selectedContent === "appointmentsByCreationUser" || selectedContent === "bookingCancellations" ? "contained" : "text"}
                onClick={handleReportsMenuOpen}
                sx={{ 
                  mr: 1,
                  bgcolor: (selectedContent === "reports" || selectedContent === "customReport" || selectedContent === "vaccinationReport" || selectedContent === "groomerMatrix" || selectedContent === "groomerRebooks" || selectedContent === "marketingLeadsVsBookings" || selectedContent === "groomerProduction" || selectedContent === "appointmentsByReferral" || selectedContent === "appointmentsByPetSize" || selectedContent === "appointmentsWithGroomerChange" || selectedContent === "appointmentsByCreationUser" || selectedContent === "bookingCancellations") ? '#1976d2' : 'transparent',
                  color: (selectedContent === "reports" || selectedContent === "customReport" || selectedContent === "vaccinationReport" || selectedContent === "groomerMatrix" || selectedContent === "groomerRebooks" || selectedContent === "marketingLeadsVsBookings" || selectedContent === "groomerProduction" || selectedContent === "appointmentsByReferral" || selectedContent === "appointmentsByPetSize" || selectedContent === "appointmentsWithGroomerChange" || selectedContent === "appointmentsByCreationUser" || selectedContent === "bookingCancellations") ? 'white' : '#666',
                  '&:hover': {
                    bgcolor: (selectedContent === "reports" || selectedContent === "customReport" || selectedContent === "vaccinationReport" || selectedContent === "groomerMatrix" || selectedContent === "groomerRebooks" || selectedContent === "marketingLeadsVsBookings" || selectedContent === "groomerProduction" || selectedContent === "appointmentsByReferral" || selectedContent === "appointmentsByPetSize" || selectedContent === "appointmentsWithGroomerChange" || selectedContent === "appointmentsByCreationUser" || selectedContent === "bookingCancellations") ? '#1565c0' : '#f5f5f5'
                  }
                }}
              >
                Reports
              </Button>
              <Menu
                anchorEl={reportsAnchorEl}
                open={Boolean(reportsAnchorEl)}
                onClose={handleReportsMenuClose}
                PaperProps={{
                  elevation: 2,
                  sx: { mt: 1 }
                }}
              >
                <MenuItem onClick={() => handleReportsSelect("vaccinationReport")}>
                  Vaccination Status by Appointment Date
                </MenuItem>
                <MenuItem onClick={() => handleReportsSelect("groomerMatrix")}>
                  Groomer Matrix
                </MenuItem>
                <MenuItem onClick={() => handleReportsSelect("groomerRebooks")}>
                  Groomer Rebooks
                </MenuItem>
                <MenuItem onClick={() => handleReportsSelect("marketingLeadsVsBookings")}>
                  Marketing Leads vs Bookings
                </MenuItem>
                <MenuItem onClick={() => handleReportsSelect("groomerProduction")}>
                  Groomer Production Report
                </MenuItem>
                <MenuItem onClick={() => handleReportsSelect("appointmentsByReferral")}>
                  Appointments by Referral Source
                </MenuItem>
                <MenuItem onClick={() => handleReportsSelect("appointmentsByPetSize")}>
                  Appointments by Pet Size
                </MenuItem>
                <MenuItem onClick={() => handleReportsSelect("appointmentsWithGroomerChange")}>
                  Appointments with Groomer Change
                </MenuItem>
                <MenuItem onClick={() => handleReportsSelect("appointmentsByCreationUser")}>
                  Appointments Booked
                </MenuItem>
                <MenuItem onClick={() => handleReportsSelect("bookingCancellations")}>
                  Booking Cancellations
                </MenuItem>
              </Menu>
              <Button
                variant={selectedContent === "documents" ? "contained" : "text"}
                onClick={handleDocumentsSelect}
                sx={{ 
                  mr: 1,
                  bgcolor: selectedContent === "documents" ? '#1976d2' : 'transparent',
                  color: selectedContent === "documents" ? 'white' : '#666',
                  '&:hover': {
                    bgcolor: selectedContent === "documents" ? '#1565c0' : '#f5f5f5'
                  }
                }}
              >
                Documents
              </Button>
              <Button
                variant={(selectedContent === "maintenance" || selectedContent === "moegoScheduler" || selectedContent === "facebookDownloader") ? "contained" : "text"}
                onClick={handleMaintenanceMenuOpen}
                sx={{ 
                  mr: 1,
                  bgcolor: (selectedContent === "maintenance" || selectedContent === "moegoScheduler" || selectedContent === "facebookDownloader") ? '#1976d2' : 'transparent',
                  color: (selectedContent === "maintenance" || selectedContent === "moegoScheduler" || selectedContent === "facebookDownloader") ? 'white' : '#666',
                  '&:hover': {
                    bgcolor: (selectedContent === "maintenance" || selectedContent === "moegoScheduler" || selectedContent === "facebookDownloader") ? '#1565c0' : '#f5f5f5'
                  }
                }}
              >
                Maintenance
              </Button>
              <Menu
                anchorEl={maintenanceAnchorEl}
                open={Boolean(maintenanceAnchorEl)}
                onClose={handleMaintenanceMenuClose}
                PaperProps={{
                  elevation: 2,
                  sx: { mt: 1 }
                }}
              >
                <MenuItem onClick={() => handleMaintenanceSelect("maintenance")}>
                  Load SOURCE_DETAIL_DATA
                </MenuItem>
                <MenuItem onClick={() => handleMaintenanceSelect("sqlQueryTool")}>
                  SQL Query Tool
                </MenuItem>
                <MenuItem onClick={() => handleMaintenanceSelect("moegoScheduler")}>
                  MoeGo Scheduler
                </MenuItem>
                <MenuItem onClick={() => handleMaintenanceSelect("facebookDownloader")}>
                  Facebook Message Downloader
                </MenuItem>
                {/* Facebook AI Response Testing removed - functionality now in Facebook Message Manager */}
                <MenuItem onClick={() => handleMaintenanceSelect("userManagement")}>
                  User Management
                </MenuItem>
                <MenuItem onClick={() => handleMaintenanceSelect("notificationReset")}>
                  Reset Notification Registration
                </MenuItem>
                <MenuItem onClick={() => handleMaintenanceSelect("serviceAreaManagement")}>
                  Service Area Management
                </MenuItem>
                <MenuItem onClick={() => handleMaintenanceSelect("loadServiceAreaData")}>
                  Load Service Area Data
                </MenuItem>
                <MenuItem onClick={() => handleMaintenanceSelect("testNotifications")}>
                  Test Notifications
                </MenuItem>
              </Menu>
              <Box sx={{ flexGrow: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ mr: 1, color: '#666' }}>
                  {username}
                </Typography>
                <IconButton
                  onClick={handleUserMenuOpen}
                  sx={{ color: '#666' }}
                >
                  <AccountCircleIcon />
                </IconButton>
              </Box>
            </>
          )}
          <Menu
            anchorEl={userMenuAnchorEl}
            open={Boolean(userMenuAnchorEl)}
            onClose={handleUserMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleChangePasswordClick}>
              Change Password
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
      >
        <Box
          sx={{ width: 250 }}
          role="presentation"
        >
          <List>
            {/* Main Menu Item - Dashboard */}
            <ListItem 
              button 
              onClick={() => {
                handleDashboardSelect();
                handleMobileMenuClose();
              }}
              selected={selectedContent === ""}
            >
              <ListItemText 
                primary="DASHBOARD" 
                primaryTypographyProps={{ fontWeight: 'bold' }}
              />
            </ListItem>
            
            {/* Main Menu Item - Applications */}
            <ListItem
              sx={{ mt: 1 }}
              button
              onClick={() => {
                // Toggle visibility of Applications submenu items
                handleMobileMenuClose();
              }}
            >
              <ListItemText 
                primary="APPLICATIONS" 
                primaryTypographyProps={{ fontWeight: 'bold' }}
              />
            </ListItem>
            
            {/* Applications Sub-items */}
            <ListItem button onClick={() => {
              handleApplicationsSelect("scheduler");
              handleMobileMenuClose();
            }}
            selected={selectedContent === "scheduler"}
            sx={{ pl: 4 }}>
              <ListItemText primary="Groomer Scheduler" />
            </ListItem>
            
            <ListItem button onClick={() => {
              handleApplicationsSelect("taskTracker");
              handleMobileMenuClose();
            }}
            selected={selectedContent === "taskTracker"}
            sx={{ pl: 4 }}>
              <ListItemText primary="Task Tracker" />
            </ListItem>
            
            <ListItem button onClick={() => {
              handleApplicationsSelect("marketingStats");
              handleMobileMenuClose();
            }}
            selected={selectedContent === "marketingStats"}
            sx={{ pl: 4 }}>
              <ListItemText primary="Daily Marketing Statistic" />
            </ListItem>
            
            <ListItem button onClick={() => {
              handleApplicationsSelect("facebookMessageManager");
              handleMobileMenuClose();
            }}
            selected={selectedContent === "facebookMessageManager"}
            sx={{ pl: 4 }}>
              <ListItemText primary="Facebook Message Manager" />
            </ListItem>
            
            
            
            
            {/* Main Menu Item - Reports */}
            <ListItem
              sx={{ mt: 1 }}
              button
              onClick={() => {
                handleMobileMenuClose();
              }}
            >
              <ListItemText 
                primary="REPORTS" 
                primaryTypographyProps={{ fontWeight: 'bold' }}
              />
            </ListItem>
            
            {/* Reports Sub-items */}
            <ListItem button onClick={() => {
              handleReportsSelect("vaccinationReport");
              handleMobileMenuClose();
            }}
            selected={selectedContent === "vaccinationReport"}
            sx={{ pl: 4 }}>
              <ListItemText primary="Vaccination Status" />
            </ListItem>
            
            <ListItem button onClick={() => {
              handleReportsSelect("groomerMatrix");
              handleMobileMenuClose();
            }}
            selected={selectedContent === "groomerMatrix"}
            sx={{ pl: 4 }}>
              <ListItemText primary="Groomer Matrix" />
            </ListItem>
            
            <ListItem button onClick={() => {
              handleReportsSelect("groomerRebooks");
              handleMobileMenuClose();
            }}
            selected={selectedContent === "groomerRebooks"}
            sx={{ pl: 4 }}>
              <ListItemText primary="Groomer Rebooks" />
            </ListItem>
            
            <ListItem button onClick={() => {
              handleReportsSelect("marketingLeadsVsBookings");
              handleMobileMenuClose();
            }}
            selected={selectedContent === "marketingLeadsVsBookings"}
            sx={{ pl: 4 }}>
              <ListItemText primary="Marketing Leads vs Bookings" />
            </ListItem>
            
            <ListItem button onClick={() => {
              handleReportsSelect("groomerProduction");
              handleMobileMenuClose();
            }}
            selected={selectedContent === "groomerProduction"}
            sx={{ pl: 4 }}>
              <ListItemText primary="Groomer Production Report" />
            </ListItem>
            
            <ListItem button onClick={() => {
              handleReportsSelect("appointmentsByReferral");
              handleMobileMenuClose();
            }}
            selected={selectedContent === "appointmentsByReferral"}
            sx={{ pl: 4 }}>
              <ListItemText primary="Appointments by Referral Source" />
            </ListItem>
            
            <ListItem button onClick={() => {
              handleReportsSelect("appointmentsByPetSize");
              handleMobileMenuClose();
            }}
            selected={selectedContent === "appointmentsByPetSize"}
            sx={{ pl: 4 }}>
              <ListItemText primary="Appointments by Pet Size" />
            </ListItem>
            
            <ListItem button onClick={() => {
              handleReportsSelect("appointmentsWithGroomerChange");
              handleMobileMenuClose();
            }}
            selected={selectedContent === "appointmentsWithGroomerChange"}
            sx={{ pl: 4 }}>
              <ListItemText primary="Appointments with Groomer Change" />
            </ListItem>
            
            <ListItem button onClick={() => {
              handleReportsSelect("appointmentsByCreationUser");
              handleMobileMenuClose();
            }}
            selected={selectedContent === "appointmentsByCreationUser"}
            sx={{ pl: 4 }}>
              <ListItemText primary="Appointments Booked" />
            </ListItem>
            
            <ListItem button onClick={() => {
              handleReportsSelect("bookingCancellations");
              handleMobileMenuClose();
            }}
            selected={selectedContent === "bookingCancellations"}
            sx={{ pl: 4 }}>
              <ListItemText primary="Booking Cancellations" />
            </ListItem>
            
            {/* Main Menu Item - Documents */}
            <ListItem 
              sx={{ mt: 1 }}
              button 
              onClick={() => {
                handleDocumentsSelect();
                handleMobileMenuClose();
              }}
              selected={selectedContent === "documents"}
            >
              <ListItemText 
                primary="DOCUMENTS" 
                primaryTypographyProps={{ fontWeight: 'bold' }}
              />
            </ListItem>
            
            {/* Main Menu Item - Maintenance */}
            <ListItem
              sx={{ mt: 1 }}
              button
              onClick={() => {
                handleMobileMenuClose();
              }}
            >
              <ListItemText 
                primary="MAINTENANCE" 
                primaryTypographyProps={{ fontWeight: 'bold' }}
              />
            </ListItem>
            
            {/* Maintenance Sub-items */}
            <ListItem button onClick={() => {
              handleMaintenanceSelect("maintenance");
              handleMobileMenuClose();
            }}
            selected={selectedContent === "maintenance"}
            sx={{ pl: 4 }}>
              <ListItemText primary="Load SOURCE_DETAIL_DATA" />
            </ListItem>
            
            <ListItem button onClick={() => {
              handleMaintenanceSelect("sqlQueryTool");
              handleMobileMenuClose();
            }}
            selected={selectedContent === "sqlQueryTool"}
            sx={{ pl: 4 }}>
              <ListItemText primary="SQL Query Tool" />
            </ListItem>
            
            <ListItem button onClick={() => {
              handleMaintenanceSelect("moegoScheduler");
              handleMobileMenuClose();
            }}
            selected={selectedContent === "moegoScheduler"}
            sx={{ pl: 4 }}>
              <ListItemText primary="MoeGo Scheduler" />
            </ListItem>
            
            <ListItem button onClick={() => {
              handleMaintenanceSelect("facebookDownloader");
              handleMobileMenuClose();
            }}
            selected={selectedContent === "facebookDownloader"}
            sx={{ pl: 4 }}>
              <ListItemText primary="Facebook Message Downloader" />
            </ListItem>
            
            {/* Facebook AI Response Testing removed - functionality now in Facebook Message Manager */}
            
            <ListItem button onClick={() => {
              handleMaintenanceSelect("userManagement");
              handleMobileMenuClose();
            }}
              selected={selectedContent === "userManagement"}
              sx={{ pl: 4 }}>
              <ListItemText primary="User Management" />
            </ListItem>
            
            <ListItem button onClick={() => {
              handleMaintenanceSelect("notificationReset");
              handleMobileMenuClose();
            }}
            selected={selectedContent === "notificationReset"}
            sx={{ pl: 4 }}>
              <ListItemText primary="Reset Notification Registration" />
            </ListItem>
            
            <ListItem button onClick={() => {
              handleMaintenanceSelect("serviceAreaManagement");
              handleMobileMenuClose();
            }}
            selected={selectedContent === "serviceAreaManagement"}
            sx={{ pl: 4 }}>
              <ListItemText primary="Service Area Management" />
            </ListItem>
            
            <ListItem button onClick={() => {
              handleMaintenanceSelect("loadServiceAreaData");
              handleMobileMenuClose();
            }}
            selected={selectedContent === "loadServiceAreaData"}
            sx={{ pl: 4 }}>
              <ListItemText primary="Load Service Area Data" />
            </ListItem>
            
            <ListItem button onClick={() => {
              handleMaintenanceSelect("testNotifications");
              handleMobileMenuClose();
            }}
            selected={selectedContent === "testNotifications"}
            sx={{ pl: 4 }}>
              <ListItemText primary="Test Notifications" />
            </ListItem>
          </List>
        </Box>
      </Drawer>
      
      {/* Change Password Dialog */}
      <ChangePassword 
        open={changePasswordOpen} 
        onClose={handleChangePasswordClose} 
      />
      {/* Logo section below menu bar */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        p: 2,
        bgcolor: '#f8f8f8',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body1" sx={{ mr: 1, color: '#666' }}>
            Greater Tampa Bay
          </Typography>
          <img src="/logo.png" alt="Logo" style={{ height: '32px' }} />
        </Box>
      </Box>

      <Box sx={{ height: "calc(100vh - 112px)", overflow: "auto" }}>
        <Box sx={{ p: 2, position: "relative" }}>
          {selectedContent === "" && (
            <>
              <LastUpdateInfo key={refreshKey} />
              <Box sx={{ mb: 3, mt: 2, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: isMobile ? '1.05rem' : undefined }}>
                  Zoomin Groomin - Performance Dashboard
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TodaysTotals key={refreshKey} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <GroomerPerformance key={refreshKey} />
                </Grid>
              </Grid>
            </>
          )}
          {renderContent()}
        </Box>
      </Box>
    </Box>
  );
};

// KPI Dashboard Component
const DashboardKPI = () => {
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useMockData, setUseMockData] = useState(false);

  // Date calculations for 7-day windows (excluding today)
  const today = new Date();
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  // Format date range for display (e.g., "Mar 1 - Mar 7, 2024")
  const formatDateRange = (startDate, endDate) => {
    return `${startDate.toLocaleDateString('en-US', { 
      month: 'short',
      day: 'numeric'
    })} - ${endDate.toLocaleDateString('en-US', { 
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })}`;
  };

  // Current period: 7 days before today (not including today) to yesterday
  const currentPeriodEnd = new Date(today);
  currentPeriodEnd.setDate(currentPeriodEnd.getDate() - 1);
  currentPeriodEnd.setHours(23, 59, 59, 999);
  
  const currentPeriodStart = new Date(today);
  currentPeriodStart.setDate(currentPeriodStart.getDate() - 7);
  currentPeriodStart.setHours(0, 0, 0, 0);

  // Previous period: 14 to 8 days before today
  const previousPeriodEnd = new Date(currentPeriodStart);
  previousPeriodEnd.setDate(previousPeriodEnd.getDate() - 1);
  previousPeriodEnd.setHours(23, 59, 59, 999);
  
  const previousPeriodStart = new Date(previousPeriodEnd);
  previousPeriodStart.setDate(previousPeriodStart.getDate() - 6);
  previousPeriodStart.setHours(0, 0, 0, 0);

  const currentDateRange = formatDateRange(currentPeriodStart, currentPeriodEnd);
  const previousDateRange = formatDateRange(previousPeriodStart, previousPeriodEnd);
  
  const generateDateString = (date) => {
    return date.toISOString().split('T')[0];
  };
  
  const generateMockDay = (date, weekType) => {
    const dateStr = generateDateString(date);
    
    // For future dates, use 'N/A'
    const isFutureDate = date > today;
    
    return {
      date: dateStr,
      day_name: dayNames[date.getDay()],
      week: weekType,
      num_appointments: isFutureDate ? 'N/A' : Math.floor(Math.random() * 10) + 5,
      num_pets: isFutureDate ? 'N/A' : Math.floor(Math.random() * 15) + 10,
      total_collected_price: isFutureDate ? 'N/A' : Math.floor(Math.random() * 1000) + 500
    };
  };
  
  // Generate data for both periods
  const mockData = [
    // Current period (today to 6 days ago)
    ...Array.from({ length: 7 }, (_, i) => {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      return generateMockDay(date, 'current');
    }),
    
    // Previous period (7 days ago to 13 days ago)
    ...Array.from({ length: 7 }, (_, i) => {
      const date = new Date(previousPeriodEnd);
      date.setDate(previousPeriodEnd.getDate() - i);
      return generateMockDay(date, 'previous');
    })
  ];
  
  // Function to fetch KPI data - defined outside useEffect so it can be called from retry button
  const fetchKPIData = async () => {
    setLoading(true);
    console.log('⏳ Attempting to fetch KPI data from /api/kpis endpoint...');
    try {
      // Use the apiFetch utility to ensure proper URL resolution on all devices
      const response = await apiFetch('/api/reports/kpis', {
        method: 'GET'
      });
      console.log('🔍 Response status details:', response.status, response.statusText);
      console.log('📡 Response received:', response.status, response.statusText);
      
      // Check if response is ok
      if (!response.ok) {
        console.error('❌ Response not OK:', response.status, response.statusText);
        throw new Error(`Failed to fetch KPI data: ${response.status} ${response.statusText}`);
      }
      
      // Check content type
      const contentType = response.headers.get('content-type');
      console.log('📄 Content-Type:', contentType);
      
      if (!contentType || !contentType.includes('application/json')) {
        console.error('⚠️ API did not return JSON. Content-Type:', contentType);
        
        // Get the actual response text for diagnosis
        const text = await response.text();
        console.error('📃 Response text (first 200 chars):', text.substring(0, 200));
        
        // Check if the error is because of a missing table
        if (text.includes('no such table') && text.includes('appointments')) {
          console.error('❌ Appointments table does not exist in the database');
          setError('Missing appointments table in the database');
        }
        
        console.log('🔄 Falling back to mock data');
        setKpiData(mockData);
        setUseMockData(true);
        setLoading(false);
        return;
      }
      
      // Try to parse the JSON response
      try {
        const data = await response.json();
        console.log('✅ Received JSON data:', data);
        
        // If the data is empty, use mock data
        if (!data || data.length === 0) {
          console.warn('⚠️ API returned empty data array, using mock data instead');
          setKpiData(mockData);
          setUseMockData(true);
        } else {
          console.log('✅ Using actual API data');
          setKpiData(data);
          setUseMockData(false);
        }
      } catch (jsonError) {
        console.error('❌ JSON parsing error:', jsonError);
        console.log('🔄 Falling back to mock data');
        setKpiData(mockData);
        setUseMockData(true);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('❌ Error fetching KPI data:', err);
      
      // Check if the error message mentions a database table
      if (err.message && (
          err.message.includes('no such table') || 
          err.message.includes('appointments') ||
          err.message.toLowerCase().includes('database')
      )) {
        console.error('❌ Database schema issue detected - appointments table may be missing');
        setError('Missing database table');
      }
      
      console.log('🔄 Falling back to mock data');
      setKpiData(mockData);
      setUseMockData(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKPIData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Process the data to match rolling window dates
  const currentWeek = [];
  const previousWeek = [];

  if (kpiData && kpiData.length > 0) {
    // Convert current period range to strings for comparison
    const currentStart = generateDateString(currentPeriodStart);
    const currentEnd = generateDateString(currentPeriodEnd);
    const previousStart = generateDateString(previousPeriodStart);
    const previousEnd = generateDateString(previousPeriodEnd);

    // Filter and sort the data for both periods
    kpiData.forEach(item => {
      const itemDate = item.date;
      // Ensure exact date range matching
      if (itemDate >= currentStart && itemDate <= currentEnd) {
        currentWeek.push({...item, week: 'current'});
      } else if (itemDate >= previousStart && itemDate <= previousEnd) {
        previousWeek.push({...item, week: 'previous'});
      }
    });
    
    // Sort by date descending (newest first)
    currentWeek.sort((a, b) => new Date(b.date) - new Date(a.date));
    previousWeek.sort((a, b) => new Date(b.date) - new Date(a.date));
  }

  // Calculate totals for each week, properly handling 'N/A' values and types
  const calculateTotals = (weekData) => {
    return weekData.reduce((acc, item) => {
      // Skip future dates with 'N/A' values entirely from calculations
      if (item.num_pets === 'N/A') {
        return acc; // Don't include future dates in totals
      }
      
      // Handle different types properly (API may return numbers or strings)
      const numAppointments = typeof item.num_appointments === 'number' 
        ? item.num_appointments 
        : (parseInt(item.num_appointments) || 0);
        
      const numPets = typeof item.num_pets === 'number'
        ? item.num_pets
        : (parseInt(item.num_pets) || 0);
        
      const collectedPrice = typeof item.total_collected_price === 'number'
        ? item.total_collected_price
        : (parseFloat(item.total_collected_price) || 0);
      
      // Handle booking metrics
      // Handle booking metrics for non-groomer staff
      const nonGroomerAppointments = typeof item.non_groomer_appointments === 'number'
        ? item.non_groomer_appointments
        : (parseInt(item.non_groomer_appointments) || 0);
        
      const nonGroomerPets = typeof item.non_groomer_pets === 'number'
        ? item.non_groomer_pets
        : (parseInt(item.non_groomer_pets) || 0);
        
      // Handle booking metrics for groomer staff
      const groomerAppointments = typeof item.groomer_appointments === 'number'
        ? item.groomer_appointments
        : (parseInt(item.groomer_appointments) || 0);
        
      const groomerPets = typeof item.groomer_pets === 'number'
        ? item.groomer_pets
        : (parseInt(item.groomer_pets) || 0);

      return {
        appointments: acc.appointments + numAppointments,
        pets: acc.pets + numPets,
        revenue: acc.revenue + collectedPrice,
        non_groomer_appointments: acc.non_groomer_appointments + nonGroomerAppointments,
        non_groomer_pets: acc.non_groomer_pets + nonGroomerPets,
        groomer_appointments: acc.groomer_appointments + groomerAppointments,
        groomer_pets: acc.groomer_pets + groomerPets
      };
    }, { appointments: 0, pets: 0, revenue: 0, non_groomer_appointments: 0, non_groomer_pets: 0, groomer_appointments: 0, groomer_pets: 0 });
  };

  const currentWeekTotals = calculateTotals(currentWeek);
  const previousWeekTotals = calculateTotals(previousWeek);

  // Calculate percent change
  const getPercentChange = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return previous > 0 ? Math.round(((current - previous) / previous) * 100) : 0;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };
  
  // Format date string without timezone shift to ensure correct day display
  const formatDateWithoutTimezoneShift = (dateString) => {
    // Parse date parts directly to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
    
    // Create date object with exact year, month (0-indexed), and day
    const date = new Date(year, month - 1, day);
    
    // Format with weekday, month, and day
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'short', 
      day: 'numeric'
    });
  };

  return (
    <>
      <Box sx={{ mt: 2, mb: 2, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Prior Two Week Results
        </Typography>
        <Box sx={{ mt: 1, mb: 2 }}>
        <Typography variant="subtitle1" color="text.secondary">
          Current Period (Last 7 Days) • {currentDateRange}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Previous Period • {previousDateRange}
        </Typography>
        </Box>
        {useMockData && (
          <Box>
            <Typography variant="subtitle2" color="warning.main" sx={{ mt: 1 }}>
              (Using demonstration data - SOURCE_DETAIL_DATA table may be empty or unavailable)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              The KPI data is currently unavailable. This demo shows how the dashboard will look once data is available.
            </Typography>
            <Button 
              size="small" 
              variant="outlined" 
              color="primary" 
              onClick={() => fetchKPIData()}
              sx={{ mt: 1, mb: 1 }}
            >
              Retry API Connection
            </Button>
          </Box>
        )}
      </Box>

      <Grid container spacing={2}>
        {/* Appointments Card */}
        <Grid item xs={12} sm={6} md={1.5}>
          <Card elevation={3}>
            <CardHeader
              title="APPOINTMENTS COMPLETED <br>" 
              titleTypographyProps={{ 
                align: 'center', 
                variant: 'subtitle1',
                sx: { fontSize: '0.9rem', fontWeight: 'bold', color: 'white' }
              }}
              sx={{ backgroundColor: '#42a5f5' }}
            />
            <CardContent sx={{ py: 1, px: 1 }}>
              <Typography variant="h6" color="primary" align="center" sx={{ fontSize: '1.15rem' }} gutterBottom>
                {currentWeekTotals.appointments}
              </Typography>
              <Divider />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                  Previous Week
                </Typography>
                <Typography variant="body2" 
                  color={getPercentChange(currentWeekTotals.appointments, previousWeekTotals.appointments) >= 0 ? 'success.main' : 'error.main'}
                  sx={{ fontSize: '0.8rem' }}>
                  {previousWeekTotals.appointments}
                  {' '}
                  ({getPercentChange(currentWeekTotals.appointments, previousWeekTotals.appointments) >= 0 ? '+' : ''}
                  {getPercentChange(currentWeekTotals.appointments, previousWeekTotals.appointments)}%)
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Pets Card */}
        <Grid item xs={12} sm={6} md={1.5}>
          <Card elevation={3}>
            <CardHeader
              title="NUM PETS COMPLETED <br>"
              titleTypographyProps={{ 
                align: 'center', 
                variant: 'subtitle1',
                sx: { fontSize: '0.9rem', fontWeight: 'bold', color: 'white' }
              }}
              sx={{ backgroundColor: '#42a5f5' }}
            />
            <CardContent sx={{ py: 1, px: 1 }}>
              <Typography variant="h6" color="primary" align="center" sx={{ fontSize: '1.15rem' }} gutterBottom>
                {currentWeekTotals.pets}
              </Typography>
              <Divider />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                  Previous Week
                </Typography>
                <Typography variant="body2" 
                  color={getPercentChange(currentWeekTotals.pets, previousWeekTotals.pets) >= 0 ? 'success.main' : 'error.main'}
                  sx={{ fontSize: '0.8rem' }}>
                  {previousWeekTotals.pets}
                  {' '}
                  ({getPercentChange(currentWeekTotals.pets, previousWeekTotals.pets) >= 0 ? '+' : ''}
                  {getPercentChange(currentWeekTotals.pets, previousWeekTotals.pets)}%)
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue Card */}
        <Grid item xs={12} sm={6} md={1.5}>
          <Card elevation={3}>
            <CardHeader
              title="COLLECTED PRICE (no tips)"
              titleTypographyProps={{ 
                align: 'center', 
                variant: 'subtitle1',
                sx: { fontSize: '0.9rem', fontWeight: 'bold', color: 'white' }
              }}
              sx={{ backgroundColor: '#42a5f5' }}
            />
            <CardContent sx={{ py: 1, px: 1 }}>
              <Typography variant="h6" color="primary" align="center" sx={{ fontSize: '1.15rem' }} gutterBottom>
                {formatCurrency(currentWeekTotals.revenue)}
              </Typography>
              <Divider />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                  Previous Week
                </Typography>
                <Typography variant="body2" 
                  color={getPercentChange(currentWeekTotals.revenue, previousWeekTotals.revenue) >= 0 ? 'success.main' : 'error.main'}
                  sx={{ fontSize: '0.8rem' }}>
                  {formatCurrency(previousWeekTotals.revenue)}
                  {' '}
                  ({getPercentChange(currentWeekTotals.revenue, previousWeekTotals.revenue) >= 0 ? '+' : ''}
                  {getPercentChange(currentWeekTotals.revenue, previousWeekTotals.revenue)}%)
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Average Service Price Card */}
        <Grid item xs={12} sm={6} md={1.5}>
          <Card elevation={3}>
            <CardHeader
              title="AVERAGE GROOM PRICE"
              titleTypographyProps={{ 
                align: 'center', 
                variant: 'subtitle1',
                sx: { fontSize: '0.9rem', fontWeight: 'bold', color: 'white' }
              }}
              sx={{ backgroundColor: '#42a5f5' }}
            />
            <CardContent sx={{ py: 1, px: 1 }}>
              <Typography variant="h6" color="primary" align="center" sx={{ fontSize: '1.15rem' }} gutterBottom>
                {currentWeekTotals.pets > 0 
                  ? formatCurrency(currentWeekTotals.revenue / currentWeekTotals.pets)
                  : 'N/A'}
              </Typography>
              <Divider />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                  Previous Week
                </Typography>
                <Typography variant="body2" 
                  color={getPercentChange(
                    currentWeekTotals.pets > 0 ? currentWeekTotals.revenue / currentWeekTotals.pets : 0,
                    previousWeekTotals.pets > 0 ? previousWeekTotals.revenue / previousWeekTotals.pets : 0
                  ) >= 0 ? 'success.main' : 'error.main'}
                  sx={{ fontSize: '0.8rem' }}>
                  {previousWeekTotals.pets > 0 
                    ? formatCurrency(previousWeekTotals.revenue / previousWeekTotals.pets)
                    : 'N/A'}
                  {' '}
                  {previousWeekTotals.pets > 0 && currentWeekTotals.pets > 0 && (
                    `(${getPercentChange(
                      currentWeekTotals.revenue / currentWeekTotals.pets,
                      previousWeekTotals.revenue / previousWeekTotals.pets
                    ) >= 0 ? '+' : ''}${getPercentChange(
                      currentWeekTotals.revenue / currentWeekTotals.pets,
                      previousWeekTotals.revenue / previousWeekTotals.pets
                    )}%)`
                  )}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Non Groomer (STAFF) Appointments Card */}
        <Grid item xs={12} sm={6} md={1.5}>
          <Card elevation={3}>
            <CardHeader
              title="APPOINTMENTS BOOKED BY STAFF"
              titleTypographyProps={{ 
                align: 'center', 
                variant: 'subtitle1',
                sx: { fontSize: '0.9rem', fontWeight: 'bold', color: 'white' }
              }}
              sx={{ backgroundColor: '#42a5f5' }}
            />
            <CardContent sx={{ py: 1, px: 1 }}>
              <Typography variant="h6" color="primary" align="center" sx={{ fontSize: '1.15rem' }} gutterBottom>
                {currentWeekTotals.non_groomer_appointments || 0}
              </Typography>
              <Divider />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                  Previous Week
                </Typography>
                <Typography variant="body2" 
                  color={getPercentChange(currentWeekTotals.non_groomer_appointments || 0, previousWeekTotals.non_groomer_appointments || 0) >= 0 ? 'success.main' : 'error.main'}
                  sx={{ fontSize: '0.8rem' }}>
                  {previousWeekTotals.non_groomer_appointments || 0}
                  {' '}
                  ({getPercentChange(currentWeekTotals.non_groomer_appointments || 0, previousWeekTotals.non_groomer_appointments || 0) >= 0 ? '+' : ''}
                  {getPercentChange(currentWeekTotals.non_groomer_appointments || 0, previousWeekTotals.non_groomer_appointments || 0)}%)
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Non Groomer (STAFF) Pets Card */}
        <Grid item xs={12} sm={6} md={1.5}>
          <Card elevation={3}>
            <CardHeader
              title="NUM PETS BOOKED BY STAFF"
              titleTypographyProps={{ 
                align: 'center', 
                variant: 'subtitle1',
                sx: { fontSize: '0.9rem', fontWeight: 'bold', color: 'white' }
              }}
              sx={{ backgroundColor: '#42a5f5' }}
            />
            <CardContent sx={{ py: 1, px: 1 }}>
              <Typography variant="h6" color="primary" align="center" sx={{ fontSize: '1.15rem' }} gutterBottom>
                {currentWeekTotals.non_groomer_pets || 0}
              </Typography>
              <Divider />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                  Previous Week
                </Typography>
                <Typography variant="body2" 
                  color={getPercentChange(currentWeekTotals.non_groomer_pets || 0, previousWeekTotals.non_groomer_pets || 0) >= 0 ? 'success.main' : 'error.main'}
                  sx={{ fontSize: '0.8rem' }}>
                  {previousWeekTotals.non_groomer_pets || 0}
                  {' '}
                  ({getPercentChange(currentWeekTotals.non_groomer_pets || 0, previousWeekTotals.non_groomer_pets || 0) >= 0 ? '+' : ''}
                  {getPercentChange(currentWeekTotals.non_groomer_pets || 0, previousWeekTotals.non_groomer_pets || 0)}%)
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Groomer Appointments Card */}
        <Grid item xs={12} sm={6} md={1.5}>
          <Card elevation={3}>
            <CardHeader
              title="APPTS BOOKED BY GROOMERS"
              titleTypographyProps={{ 
                align: 'center', 
                variant: 'subtitle1',
                sx: { fontSize: '0.9rem', fontWeight: 'bold', color: 'white' }
              }}
              sx={{ backgroundColor: '#42a5f5' }}
            />
            <CardContent sx={{ py: 1, px: 1 }}>
              <Typography variant="h6" color="primary" align="center" sx={{ fontSize: '1.15rem' }} gutterBottom>
                {currentWeekTotals.groomer_appointments || 0}
              </Typography>
              <Divider />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                  Previous Week
                </Typography>
                  <Typography variant="body2" 
                  color={getPercentChange(currentWeekTotals.groomer_appointments || 0, previousWeekTotals.groomer_appointments || 0) >= 0 ? 'success.main' : 'error.main'}
                  sx={{ fontSize: '0.8rem' }}>
                  {previousWeekTotals.groomer_appointments || 0}
                  {' '}
                  ({getPercentChange(currentWeekTotals.groomer_appointments || 0, previousWeekTotals.groomer_appointments || 0) >= 0 ? '+' : ''}
                  {getPercentChange(currentWeekTotals.groomer_appointments || 0, previousWeekTotals.groomer_appointments || 0)}%)
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Groomer Pets Card */}
        <Grid item xs={12} sm={6} md={1.5}>
          <Card elevation={3}>
            <CardHeader
              title="NUM PETS BOOKED BY GROOMERS"
              titleTypographyProps={{ 
                align: 'center', 
                variant: 'subtitle1',
                sx: { fontSize: '0.9rem', fontWeight: 'bold', color: 'white' }
              }}
              sx={{ backgroundColor: '#42a5f5' }}
            />
            <CardContent sx={{ py: 1, px: 1 }}>
              <Typography variant="h6" color="primary" align="center" sx={{ fontSize: '1.15rem' }} gutterBottom>
                {currentWeekTotals.groomer_pets || 0}
              </Typography>
              <Divider />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                  Previous Week
                </Typography>
                  <Typography variant="body2" 
                  color={getPercentChange(currentWeekTotals.groomer_pets || 0, previousWeekTotals.groomer_pets || 0) >= 0 ? 'success.main' : 'error.main'}
                  sx={{ fontSize: '0.8rem' }}>
                  {previousWeekTotals.groomer_pets || 0}
                  {' '}
                  ({getPercentChange(currentWeekTotals.groomer_pets || 0, previousWeekTotals.groomer_pets || 0) >= 0 ? '+' : ''}
                  {getPercentChange(currentWeekTotals.groomer_pets || 0, previousWeekTotals.groomer_pets || 0)}%)
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* End of KPI Cards */}
      </Grid>

      {/* Prior Week Results */}
      <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
        Prior Week Results
      </Typography>
      <Grid container spacing={1}>
        {currentWeek.length > 0 ? (
          currentWeek.map((day) => (
            <Grid item xs={12} sm={6} md={3} key={day.date}>
              <Card elevation={2}>
                <CardHeader
                  title={formatDateWithoutTimezoneShift(day.date)}
                  titleTypographyProps={{ 
                    variant: 'subtitle2',
                    sx: { color: 'white', fontSize: '0.9rem' }
                  }}
                  sx={{ backgroundColor: '#42a5f5', py: 0.5, px: 1 }}
                />
                <CardContent sx={{ py: 0.5, px: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', minWidth: '50%' }}>Appointments Completed:</Typography>
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                        {day.num_appointments === 'N/A' ? 'N/A' : (day.num_appointments || 0)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', minWidth: '50%' }}>Pets Completed:</Typography>
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                        {day.num_pets === 'N/A' ? 'N/A' : (day.num_pets || 0)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', minWidth: '50%' }}>Staff Appts Booked:</Typography>
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                        {day.non_groomer_appointments === 'N/A' ? 'N/A' : (day.non_groomer_appointments || 0)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', minWidth: '50%' }}>Staff Pets Booked:</Typography>
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                        {day.non_groomer_pets === 'N/A' ? 'N/A' : (day.non_groomer_pets || 0)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', minWidth: '50%' }}>Groomer Appts Booked:</Typography>
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                        {day.groomer_appointments === 'N/A' ? 'N/A' : (day.groomer_appointments || 0)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', minWidth: '50%' }}>Groomer Pets Booked:</Typography>
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                        {day.groomer_pets === 'N/A' ? 'N/A' : (day.groomer_pets || 0)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', minWidth: '50%' }}>Collected:</Typography>
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                        {day.total_collected_price === 'N/A' ? 'N/A' : formatCurrency(day.total_collected_price || 0)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', minWidth: '50%' }}>Avg Groom Price:</Typography>
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                        {day.num_pets === 'N/A' || day.num_pets === 0 
                          ? 'N/A' 
                          : formatCurrency(day.total_collected_price / day.num_pets)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ textAlign: "center", fontStyle: "italic" }}>
              No data available for the current week
            </Typography>
          </Grid>
        )}
      </Grid>
    </>
  );
};

// Component to display last data update timestamp
const LastUpdateInfo = () => {
  const [lastUpdate, setLastUpdate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLastUpdateInfo = async () => {
      try {
        // Use apiFetch utility to ensure proper URL resolution on all devices
        const response = await apiFetch('/api/moego/sync-info');
        
        if (!response.ok) {
          throw new Error('Failed to fetch last update info');
        }
        
        const data = await response.json();
        setLastUpdate(data?.last_sync_time || null);
      } catch (err) {
        console.error('Error fetching last update info:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLastUpdateInfo();
  }, []);

  if (loading || !lastUpdate) return null;

  // Format the date for display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (e) {
      return 'Unknown';
    }
  };

  return (
  <Box 
    sx={{ 
      position: 'relative', 
      mb: 1,
      ml: 2, 
      fontSize: '0.8rem', 
      color: 'text.secondary',
      fontStyle: 'italic'
    }}
  >
      Data Last Update: {formatDate(lastUpdate)}
    </Box>
  );
};

export default Dashboard;